import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ArrowRight } from "lucide-react";
import { adminLogin, apiErr } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuth";
import { usePageMeta } from "@/lib/meta";

export default function AdminLogin() {
  usePageMeta("Admin | Diamond's Divine Arts", "Owner administration.");
  const { admin, login } = useAdminAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (admin) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { token, username } = await adminLogin(form.username.trim(), form.password);
      login(token, username);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(apiErr(err, "Incorrect username or password"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="divine-rays relative flex min-h-screen items-center overflow-hidden bg-[#FAF7F2] py-16" data-testid="admin-login-page">
      <div className="mx-auto w-full max-w-md px-6">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="rounded-sm border border-[#D4AF37]/30 bg-white/70 p-8 shadow-sm sm:p-10"
        >
          <div className="flex items-center gap-3">
            <img src="/assets/logo.png" alt="logo" className="h-10 w-10 rounded-full object-cover ring-1 ring-[#D4AF37]/50" />
            <span className="font-display text-xs font-semibold tracking-[0.25em] text-[#1C1815]">DIAMOND'S DIVINE ARTS</span>
          </div>
          <h1 className="mt-8 font-serif text-3xl font-light text-[#1C1815]">Studio Admin</h1>
          <p className="mt-2 text-sm font-light text-[#8C7E70]">Sign in to manage your artwork, text and pricing.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
            <label className="block">
              <span className="kicker">Username</span>
              <input
                type="text"
                value={form.username}
                onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                className="field mt-2.5"
                autoComplete="username"
                data-testid="admin-username-input"
              />
            </label>
            <label className="block">
              <span className="kicker">Password</span>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                className="field mt-2.5"
                autoComplete="current-password"
                data-testid="admin-password-input"
              />
            </label>
            {error && (
              <p className="rounded-sm border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700" data-testid="admin-login-error">
                {error}
              </p>
            )}
            <button type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-60" data-testid="admin-login-submit">
              <Lock className="h-4 w-4" /> {busy ? "Signing in…" : "Sign In"} <ArrowRight className="h-4 w-4" />
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
}
